import { useContext } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import User from 'src/models/User';
import { ToastContext } from 'src/components/toast/toast';
import ProductService from 'src/services/product-service';
import { extractErrorMessage } from 'src/lib/utils';
import { CUSTOMER_SHIPPING_ADDRESS } from 'src/pages/constants';
import { AxiosError } from 'axios';

interface UseAfterpayPaymentParams {
  user: User;
  priceId: string;
  price: number;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UseAfterpayPaymentReturn {
  initiateAfterpayPayment: () => Promise<void>;
}

export const useAfterpayPayment = ({
  user,
  priceId,
  onSuccess,
  onError,
}: UseAfterpayPaymentParams): UseAfterpayPaymentReturn => {
  const { addToast } = useContext(ToastContext);

  const initiateAfterpayPayment = async (): Promise<void> => {
    try {
      // Initiate the Afterpay payment through the service
      const result = await ProductService.initiateAfterpayPaymentNutritionCoach(
        {
          userId: user.id,
          email: user.email,
          priceId,
        }
      );

      if (result.requiresPaymentCompletion && result.clientSecret) {
        await completeAfterpayPayment(result.clientSecret);
      } else {
        // Payment completed immediately (unlikely for Afterpay)
        onSuccess?.();
        addToast('Purchase completed successfully!', 'success', 5000);
      }
    } catch (error) {
      const requiresReactivation =
        (error as unknown as AxiosError)?.response?.data?.data
          ?.requiresMainSubscriptionReactivation ||
        (error as unknown as AxiosError)?.response?.data
          ?.requiresMainSubscriptionReactivation;

      const errorMessage = extractErrorMessage(
        error,
        'Afterpay payment failed'
      );

      if (!requiresReactivation) {
        addToast(errorMessage, 'danger', 5000);
      }
      onError?.(error instanceof Error ? error : new Error(errorMessage));
      throw error;
    }
  };

  const completeAfterpayPayment = async (
    clientSecret: string
  ): Promise<void> => {
    try {
      const stripe = await loadStripe(
        process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
      );

      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      await stripe.confirmAfterpayClearpayPayment(clientSecret, {
        payment_method: {
          billing_details: {
            name: user?.firstName
              ? `${user.firstName} ${user?.lastName || ''}`.trim()
              : user?.email || '',
            email: user?.email || '',
            address: {
              country: 'AU',
            },
          },
        },
        return_url: `${window.location.origin}/account/addons?afterpay_okay=true&flow=nutrition_coach`,
        mandate_data: await ProductService.getCustomerAcceptance(),
        shipping: CUSTOMER_SHIPPING_ADDRESS,
      });

      // Note: This call initiates the redirect and doesn't return
      // Completion is handled by the parent's return URL processing
    } catch (error) {
      const errorMessage = extractErrorMessage(
        error,
        'Failed to initiate afterpay payment'
      );
      addToast(errorMessage, 'danger', 5000);
      onError?.(error instanceof Error ? error : new Error(errorMessage));
    }
  };

  return {
    initiateAfterpayPayment,
  };
};
